import { Injectable } from '@angular/core';
import { catchError, EMPTY } from 'rxjs';

import { Action, CanvasActionsService, DpuStateKey, Reducer } from '@olympus/ngx-shared';
import { ToastService } from 'shared-modules-angular/toast';

import { WindViewState } from '../../app.state';
import { OscCloudService } from '../../services/osc-cloud/osc-cloud.service';
import { SignalrService } from '../../services/signalr/signalr.service';

export interface Progress {
  filePathName: string;
  percentage: number;
}

export enum SaveFileActionType {
  SAVE_FILE_CLOUD = 'SaveFileCloud',
}

@Injectable({
  providedIn: 'root',
})
export class SaveReducer implements Reducer {
  constructor(
    private readonly _osc: OscCloudService,
    private readonly _state$: WindViewState,
    private readonly _toastService: ToastService,
    private readonly _signalr: SignalrService,
    private readonly _canvasActionsService: CanvasActionsService,
  ) {
    this._signalr.connection?.on('UploadProgress', (progress: Progress) => {
      const filePath: string = this._state$.instant(DpuStateKey.DPU_LOAD_FILE).value?.filePath ?? '';

      if (filePath === progress.filePathName && filePath !== '') {
        this._state$.patch('UploadProgress', progress.percentage);
      }
    });
  }

  public async reduce(action: Action): Promise<void> {
    if (action.type !== SaveFileActionType.SAVE_FILE_CLOUD) {
      return;
    }

    const fileid = this._state$.instant('DriveFileId').value;

    await this._canvasActionsService.saveFile();

    if (fileid) {
      this._osc.driveUploadFile({
        filePathName: fileid,
      }).pipe(catchError(() => {
        this._state$.patch('FileIsUploading', false);
        this._toastService.error({ description: 'Error while saving to the cloud. Make sure the server is online and try again.' });

        return EMPTY;
      })).subscribe(() => {
        this._state$.patch('FileIsUploading', false);
        this._toastService.success({ description: 'The file was successfully saved to the cloud.' });
      });
    }
  }
}
